import React from 'react';

/**
 * RemediationLoopClip
 * A different self-healing visual — a continuous remediation loop:
 *   Detect → Diagnose → Remediate → Verify  (rotates forever)
 * A comet trail orbits the loop; nodes pulse as the comet passes.
 * Centre shows a live MTTR readout that ticks down to dramatize speed.
 */
const RemediationLoopClip = () => {
  return (
    <div className="relative w-full" data-testid="remediation-loop-clip">
      <style>{`
        @keyframes rl-orbit {
          0%   { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        @keyframes rl-counter {
          0%   { content: "4.5 h"; }
          20%  { content: "12 m"; }
          45%  { content: "3.4 m"; }
          70%  { content: "9.1 s"; }
          90%, 100% { content: "1.2 s"; }
        }
        @keyframes rl-node-1 { 0%, 100% { opacity: 0.55; transform: scale(1); }  10%, 18% { opacity: 1; transform: scale(1.08); } }
        @keyframes rl-node-2 { 0%, 100% { opacity: 0.55; transform: scale(1); }  35%, 43% { opacity: 1; transform: scale(1.08); } }
        @keyframes rl-node-3 { 0%, 100% { opacity: 0.55; transform: scale(1); }  60%, 68% { opacity: 1; transform: scale(1.08); } }
        @keyframes rl-node-4 { 0%, 100% { opacity: 0.55; transform: scale(1); }  85%, 93% { opacity: 1; transform: scale(1.08); } }
        @keyframes rl-pulse {
          0%, 100% { opacity: 0.55; }
          50%      { opacity: 1; }
        }
        @keyframes rl-bar {
          0%   { width: 14%; }
          50%  { width: 88%; }
          100% { width: 14%; }
        }
        @keyframes rl-scan {
          0%   { transform: translateY(-100%); opacity: 0; }
          15%  { opacity: 0.55; }
          85%  { opacity: 0.55; }
          100% { transform: translateY(100%); opacity: 0; }
        }
        .rl-orbit  { animation: rl-orbit 6s linear infinite; transform-origin: 230px 130px; }
        .rl-node-1 { animation: rl-node-1 6s linear infinite; transform-origin: center; transform-box: fill-box; }
        .rl-node-2 { animation: rl-node-2 6s linear infinite; transform-origin: center; transform-box: fill-box; }
        .rl-node-3 { animation: rl-node-3 6s linear infinite; transform-origin: center; transform-box: fill-box; }
        .rl-node-4 { animation: rl-node-4 6s linear infinite; transform-origin: center; transform-box: fill-box; }
        .rl-pulse  { animation: rl-pulse 1.6s ease-in-out infinite; }
        .rl-bar    { animation: rl-bar 6s ease-in-out infinite; }
        .rl-scan   { animation: rl-scan 5s ease-in-out infinite; }
        .rl-counter::after { content: "1.2 s"; animation: rl-counter 6s linear infinite; }
      `}</style>

      <div className="relative rounded-2xl border border-neutral-800 bg-gradient-to-br from-neutral-950 via-black to-neutral-950 p-5 overflow-hidden">
        {/* Header bar */}
        <div className="flex items-center justify-between text-[11px]">
          <div className="flex items-center gap-2 text-neutral-400">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 rl-pulse" />
            <span className="uppercase tracking-[0.18em]">Closed-loop remediation</span>
          </div>
          <div className="flex gap-1.5">
            <span className="w-1.5 h-1.5 rounded-full bg-neutral-700" />
            <span className="w-1.5 h-1.5 rounded-full bg-neutral-700" />
            <span className="w-1.5 h-1.5 rounded-full bg-neutral-700" />
          </div>
        </div>

        {/* Background grid + beam */}
        <div aria-hidden className="absolute inset-0 opacity-[0.18] pointer-events-none" style={{
          backgroundImage: 'linear-gradient(to right, rgba(255,255,255,0.06) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,0.06) 1px, transparent 1px)',
          backgroundSize: '28px 28px',
          maskImage: 'radial-gradient(ellipse at center, black 50%, transparent 90%)',
        }} />
        <div aria-hidden className="absolute inset-x-0 top-0 h-full pointer-events-none">
          <div className="rl-scan absolute inset-x-0 h-24" style={{
            background: 'linear-gradient(to bottom, transparent, rgba(34,197,94,0.16), transparent)',
            filter: 'blur(6px)',
          }} />
        </div>

        {/* SVG loop */}
        <svg viewBox="0 0 460 260" className="relative w-full h-auto mt-3" role="img" aria-label="Closed-loop remediation animation">
          <defs>
            <radialGradient id="rl-core" cx="50%" cy="50%" r="50%">
              <stop offset="0%"  stopColor="#064e3b" />
              <stop offset="100%" stopColor="#0b1220" />
            </radialGradient>
            <linearGradient id="rl-arc" x1="0" x2="1" y1="0" y2="1">
              <stop offset="0%" stopColor="#22c55e" />
              <stop offset="100%" stopColor="#3b82f6" />
            </linearGradient>
            <filter id="rl-glow"><feGaussianBlur stdDeviation="2.2" /></filter>
          </defs>

          {/* Outer dashed orbit ring */}
          <circle cx="230" cy="130" r="92" fill="none" stroke="#1f2937" strokeDasharray="3 5" />

          {/* Phase nodes */}
          {/* Detect — top */}
          <g className="rl-node-1">
            <circle cx="230" cy="38" r="22" fill="#0b1220" stroke="#3b82f6" strokeWidth="1.5" />
            <text x="230" y="42" textAnchor="middle" fill="#dbeafe" fontSize="10.5" fontFamily="ui-sans-serif, system-ui">Detect</text>
          </g>
          {/* Diagnose — right */}
          <g className="rl-node-2">
            <circle cx="322" cy="130" r="22" fill="#0b1220" stroke="#3b82f6" strokeWidth="1.5" />
            <text x="322" y="134" textAnchor="middle" fill="#dbeafe" fontSize="10.5" fontFamily="ui-sans-serif, system-ui">Diagnose</text>
          </g>
          {/* Remediate — bottom */}
          <g className="rl-node-3">
            <circle cx="230" cy="222" r="22" fill="#0b1220" stroke="#22c55e" strokeWidth="1.6" />
            <text x="230" y="226" textAnchor="middle" fill="#bbf7d0" fontSize="10.5" fontFamily="ui-sans-serif, system-ui">Remediate</text>
          </g>
          {/* Verify — left */}
          <g className="rl-node-4">
            <circle cx="138" cy="130" r="22" fill="#0b1220" stroke="#22c55e" strokeWidth="1.6" />
            <text x="138" y="134" textAnchor="middle" fill="#bbf7d0" fontSize="10.5" fontFamily="ui-sans-serif, system-ui">Verify</text>
          </g>

          {/* Connectors arrows */}
          {[
            { d: 'M 246 47  Q 295 70  315 113' },
            { d: 'M 315 147 Q 295 190 246 213' },
            { d: 'M 214 213 Q 165 190 145 147' },
            { d: 'M 145 113 Q 165 70  214 47' },
          ].map((p, i) => (
            <path key={i} d={p.d} fill="none" stroke="#1e3a8a" strokeWidth="1" strokeDasharray="3 3" opacity="0.55" />
          ))}

          {/* Orbiting comet trail */}
          <g className="rl-orbit">
            {/* leading dot */}
            <circle cx="230" cy="38" r="4.5" fill="#34d399" filter="url(#rl-glow)" />
            <circle cx="230" cy="38" r="3" fill="#bbf7d0" />
            {/* trail dots (smaller, fading) */}
            <circle cx="265" cy="46"  r="3"   fill="#22c55e" opacity="0.7" />
            <circle cx="294" cy="66"  r="2.4" fill="#22c55e" opacity="0.5" />
            <circle cx="312" cy="92"  r="1.8" fill="#22c55e" opacity="0.35" />
          </g>

          {/* Centre — MTTR readout */}
          <g>
            <circle cx="230" cy="130" r="46" fill="url(#rl-core)" stroke="#22c55e" strokeWidth="1.4" strokeOpacity="0.7" />
            <text x="230" y="118" textAnchor="middle" fill="#86efac" fontSize="9.5" fontFamily="ui-sans-serif, system-ui">MTTR · NOW</text>
            <text x="230" y="142" textAnchor="middle" fill="#ffffff" fontSize="20" fontFamily="ui-sans-serif, system-ui" fontWeight="600">
              <tspan className="rl-counter" />
            </text>
            <text x="230" y="158" textAnchor="middle" fill="#64748b" fontSize="9" fontFamily="ui-sans-serif, system-ui">▼ from 4.5 h</text>
          </g>
        </svg>

        {/* Bottom progress + stat */}
        <div className="mt-2">
          <div className="flex items-center justify-between text-[10.5px] text-neutral-500">
            <span className="uppercase tracking-[0.14em]">Recovery progress</span>
            <span className="tabular-nums text-emerald-400">healing…</span>
          </div>
          <div className="mt-1.5 h-1 rounded-full bg-neutral-900 overflow-hidden">
            <div className="rl-bar h-full rounded-full" style={{ background: 'linear-gradient(to right, #22c55e, #3b82f6)' }} />
          </div>
          <div className="mt-2 flex items-center justify-between text-[10.5px] text-neutral-500">
            <span>auto-recovered today · <span className="text-neutral-300 tabular-nums">1,247</span></span>
            <span>playbooks fired · <span className="text-neutral-300 tabular-nums">38</span></span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RemediationLoopClip;
